/**
 * Dialog para edição de departamentos existentes
 * Inclui configurações de distribuição automática de atendimentos
 * 
 * v3.0.0: Adicionado suporte a fila de distribuição global
 * 
 * @module EditDepartmentDialog
 */
import { useState, useEffect } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Loader2, Zap, Users, RotateCw, Star, ListTodo } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';

// Schema de validação do formulário de departamento
const departmentSchema = z.object({
  name: z.string().trim().min(1, 'Nome é obrigatório').max(100, 'Nome muito longo'),
  description: z.string().trim().max(500, 'Descrição muito longa').optional().or(z.literal('')),
  is_active: z.boolean().default(true),
  // Campos de distribuição automática
  auto_distribution_enabled: z.boolean().default(false),
  distribution_method: z.enum(['round_robin', 'least_busy']).default('round_robin'),
  max_conversations_per_agent: z.number().min(0).max(100).default(10),
  // v2.0.0: Configuração de supervisores na distribuição
  supervisor_receives_distribution: z.boolean().default(true),
  // v3.0.0: Fila de distribuição global
  default_queue_id: z.string().uuid().nullable().optional(),
});

type DepartmentFormData = z.infer<typeof departmentSchema>;

// Interface do departamento com campos de distribuição
interface Department {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  auto_distribution_enabled?: boolean;
  distribution_method?: 'round_robin' | 'least_busy';
  max_conversations_per_agent?: number;
  // v2.0.0: Configuração de supervisores na distribuição
  supervisor_receives_distribution?: boolean;
  // v3.0.0: Fila de distribuição global
  default_queue_id?: string | null;
}

interface EditDepartmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  department: Department | null;
  onSuccess: () => void;
}

export function EditDepartmentDialog({ 
  open, 
  onOpenChange, 
  department,
  onSuccess 
}: EditDepartmentDialogProps) {
  const [loading, setLoading] = useState(false);
  const { currentTenant } = useAuth();

  const form = useForm<DepartmentFormData>({
    resolver: zodResolver(departmentSchema),
    defaultValues: {
      name: '',
      description: '',
      is_active: true,
      auto_distribution_enabled: false,
      distribution_method: 'round_robin',
      max_conversations_per_agent: 10,
      supervisor_receives_distribution: true,
      default_queue_id: null,
    },
  });

  // Observa se a distribuição automática está ativada
  const autoDistributionEnabled = form.watch('auto_distribution_enabled');

  // v3.0.0: Reusa RPC existente get_distribution_queues_with_stats e filtra client-side
  const { data: queues } = useQuery({
    queryKey: ['distribution-queues-chat', currentTenant?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_distribution_queues_with_stats', {
        p_tenant_hint: currentTenant!.id,
      });
      if (error) throw error;
      return (data || []).filter(
        (q: any) => q.is_active && ['chat', 'all'].includes(q.context_type),
      );
    },
    enabled: !!currentTenant?.id && autoDistributionEnabled && open,
  });

  // Carrega dados do departamento quando o dialog abre
  useEffect(() => {
    if (department && open) {
      form.reset({
        name: department.name,
        description: department.description || '',
        is_active: department.is_active,
        auto_distribution_enabled: department.auto_distribution_enabled ?? false,
        distribution_method: department.distribution_method ?? 'round_robin',
        max_conversations_per_agent: department.max_conversations_per_agent ?? 10,
        supervisor_receives_distribution: department.supervisor_receives_distribution ?? true,
        default_queue_id: department.default_queue_id ?? null,
      });
    }
  }, [department, open, form]);

  const onSubmit = async (data: DepartmentFormData) => {
    if (!department) return;

    try {
      setLoading(true);

      // Server-side: RPC update_department recebe payload JSONB
      const { error } = await supabase.rpc('update_department', {
        p_department_id: department.id,
        p_payload: {
          name: data.name,
          description: data.description || null,
          is_active: data.is_active,
          auto_distribution_enabled: data.auto_distribution_enabled,
          distribution_method: data.distribution_method,
          max_conversations_per_agent: data.max_conversations_per_agent,
          supervisor_receives_distribution: data.supervisor_receives_distribution,
          default_queue_id: data.default_queue_id || null,
        },
        p_tenant_hint: currentTenant?.id,
      });

      if (error) {
        if (error.code === '23505' || /já existe/i.test(error.message)) {
          toast.error('Já existe um departamento com este nome');
          return;
        }
        throw error;
      }

      toast.success('Departamento atualizado com sucesso');
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('Error updating department:', error);
      toast.error('Erro ao atualizar departamento');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.reset();
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Departamento</DialogTitle>
          <DialogDescription>
            Atualize as informações do departamento.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Campos básicos */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome *</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: Suporte, Vendas, Financeiro" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Descreva as responsabilidades deste departamento..."
                      className="resize-none"
                      rows={3}
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Ativo</FormLabel>
                    <FormDescription>
                      Departamentos inativos não recebem novas conversas.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Seção de Distribuição Automática */}
            <Separator className="my-4" />
            
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Zap className="h-4 w-4 text-primary" />
                <span>Distribuição Automática</span>
              </div>

              <FormField
                control={form.control}
                name="auto_distribution_enabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 bg-muted/30">
                    <div className="space-y-0.5">
                      <FormLabel className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Ativar Distribuição Automática
                      </FormLabel>
                      <FormDescription>
                        Atribui conversas automaticamente aos agentes online deste departamento.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {/* Campos condicionais - só aparecem se distribuição está ativa */}
              {autoDistributionEnabled && (
                <div className="space-y-4 pl-4 border-l-2 border-primary/30">
                  <FormField
                    control={form.control}
                    name="distribution_method"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <RotateCw className="h-4 w-4" />
                          Método de Distribuição
                        </FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o método" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="round_robin">
                              <div className="flex flex-col">
                                <span>Rodízio (Round Robin)</span>
                                <span className="text-xs text-muted-foreground">
                                  Distribui em sequência para cada agente
                                </span>
                              </div>
                            </SelectItem>
                            <SelectItem value="least_busy">
                              <div className="flex flex-col">
                                <span>Menos Ocupado</span>
                                <span className="text-xs text-muted-foreground">
                                  Atribui ao agente com menos conversas ativas
                                </span>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="max_conversations_per_agent"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Limite de Conversas por Agente</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            min={0}
                            max={100}
                            placeholder="10"
                            {...field}
                            value={field.value}
                            onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                          />
                        </FormControl>
                        <FormDescription>
                          Máximo de conversas ativas que um agente pode ter. Use 0 para ilimitado.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* v2.0.0: Configuração de supervisores na distribuição */}
                  <FormField
                    control={form.control}
                    name="supervisor_receives_distribution"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 bg-amber-500/5 border-amber-500/20">
                        <div className="space-y-0.5">
                          <FormLabel className="flex items-center gap-2">
                            <Star className="h-4 w-4 text-amber-500" />
                            Supervisores Recebem Distribuição
                          </FormLabel>
                          <FormDescription>
                            Quando ativo, supervisores do departamento também recebem conversas na fila.
                            Quando desativado, supervisores apenas visualizam e gerenciam conversas.
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {/* v3.0.0: Seletor de Fila de Distribuição Global */}
                  <FormField
                    control={form.control}
                    name="default_queue_id"
                    render={({ field }) => (
                      <FormItem className="rounded-lg border p-3 bg-primary/5 border-primary/20">
                        <FormLabel className="flex items-center gap-2">
                          <ListTodo className="h-4 w-4 text-primary" />
                          Fila de Distribuição Global (Opcional)
                        </FormLabel>
                        <Select 
                          onValueChange={(value) => field.onChange(value === 'none' ? null : value)} 
                          value={field.value || 'none'}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Usar configuração do departamento" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="none">
                              <div className="flex flex-col">
                                <span>Usar configuração do departamento</span>
                                <span className="text-xs text-muted-foreground">
                                  Distribui usando agentes vinculados ao departamento
                                </span>
                              </div>
                            </SelectItem>
                            {queues?.map((queue) => (
                              <SelectItem key={queue.id} value={queue.id}>
                                <div className="flex flex-col">
                                  <span>{queue.name}</span>
                                  <span className="text-xs text-muted-foreground">
                                    {queue.distribution_method === 'round_robin' ? 'Rodízio' : 
                                     queue.distribution_method === 'least_busy' ? 'Menos Ocupado' : 'Ponderado'}
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Ao selecionar uma fila global, a distribuição usará os agentes e 
                          configurações da fila ao invés dos agentes do departamento.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => handleOpenChange(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Salvar Alterações
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
